import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { PermissionRequired } from '@/components/PermissionRequired';
import { DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/Sheet';
import type { Project, Client, Task } from '../types';
import { FolderKanban, Plus, ListChecks, Trash2 } from 'lucide-react';

function projectStatusVariant(status: string) {
  switch (status) {
    case 'ACTIVE': return 'success' as const;
    case 'CLOSED': return 'secondary' as const;
    case 'ARCHIVED': return 'secondary' as const;
    case 'QUEUED': return 'warning' as const;
    default: return 'secondary' as const;
  }
}

export default function ProjectsPage() {
  const { t } = useTranslation();
  const { data: projects, loading: pLoading, error: pError, refetch: refetchProjects } = useApi<Project[]>(() => api('/projects'), []);
  const { data: clients, loading: cLoading } = useApi<Client[]>(() => api('/clients'), []);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [taskBillable, setTaskBillable] = useState(true);
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskError, setTaskError] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setBusy(true);
    try {
      await api('/projects', { method: 'POST', body: JSON.stringify({ name, code, clientId, status: 'QUEUED' }) });
      setName(''); setCode(''); setClientId(''); setOpen(false);
      refetchProjects();
    } catch (err: any) { setFormError(err.message); }
    finally { setBusy(false); }
  };

  const loadTasks = async (project: Project) => {
    setSelectedProject(project);
    setTasksOpen(true);
    setTasksLoading(true);
    setTaskError('');
    try {
      const data = await api<Task[]>(`/projects/${project.id}/tasks`);
      setTasks(data || []);
    } catch (err: any) {
      setTaskError(err.message);
    } finally {
      setTasksLoading(false);
    }
  };

  const createTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !taskName.trim()) return;
    setTaskBusy(true);
    setTaskError('');
    try {
      await api(`/projects/${selectedProject.id}/tasks`, {
        method: 'POST',
        body: JSON.stringify({ name: taskName.trim(), billable: taskBillable }),
      });
      setTaskName('');
      setTaskBillable(true);
      const data = await api<Task[]>(`/projects/${selectedProject.id}/tasks`);
      setTasks(data || []);
    } catch (err: any) {
      setTaskError(err.message);
    } finally {
      setTaskBusy(false);
    }
  };

  const deactivateTask = async (taskId: string) => {
    if (!selectedProject) return;
    try {
      await api(`/projects/${selectedProject.id}/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: false }),
      });
      const data = await api<Task[]>(`/projects/${selectedProject.id}/tasks`);
      setTasks(data || []);
    } catch {
      // ignore
    }
  };

  const columns = [
    { key: 'name', header: t('name') || 'Name', render: (p: Project) => (
      <Link to={`/projects/${p.id}`} className="text-sm font-medium text-[var(--color-primary)] hover:underline">
        {p.name}
      </Link>
    ) },
    { key: 'code', header: 'Code', render: (p: Project) => <span className="font-mono text-xs">{p.code}</span> },
    { key: 'client', header: t('client') || 'Client', render: (p: Project) => p.client?.name || '-' },
    { key: 'status', header: t('status') || 'Status', render: (p: Project) => <Badge variant={projectStatusVariant(p.status)}>{p.status}</Badge> },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (p: Project) => (
        <div className="flex justify-end">
          <Button data-testid="open-tasks-button" variant="ghost" size="sm" onClick={() => loadTasks(p)}>
            <ListChecks className="mr-1.5 h-4 w-4" />
            Tasks
          </Button>
        </div>
      ),
    },
  ];

  const mobileRender = (p: Project) => (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
        <FolderKanban className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--color-foreground)]">{p.name}</div>
        <div className="text-xs text-[var(--color-muted-foreground)]">{p.client?.name || '-'} · <span className="font-mono">{p.code}</span></div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant={projectStatusVariant(p.status)}>{p.status}</Badge>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => loadTasks(p)}>
          <ListChecks className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="animate-in">
      <PageHeader title={t('projects') || 'Projects'}>
        <Dialog open={open} onOpenChange={setOpen}>
          <PermissionRequired pageKey="projects" minLevel="READWRITE">
            <DialogTrigger asChild>
              <Button data-testid="create-project-button" size="sm"><Plus className="mr-2 h-4 w-4" /> New</Button>
            </DialogTrigger>
          </PermissionRequired>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Project</DialogTitle><DialogDescription>Add a new project to your workspace.</DialogDescription></DialogHeader>
            <form id="project-form" onSubmit={handleCreate} className="space-y-4 py-2">
              {formError && <Badge variant="destructive" className="w-full justify-center">{formError}</Badge>}
              <div className="space-y-2"><Label>Name</Label><Input value={name} onChange={e=>setName(e.target.value)} required /></div>
              <div className="space-y-2"><Label>Code</Label><Input value={code} onChange={e=>setCode(e.target.value)} required /></div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={clientId} onValueChange={setClientId} required>
                  <SelectTrigger><SelectValue placeholder={cLoading ? t('loading') : 'Select client'} /></SelectTrigger>
                  <SelectContent>
                    {(clients || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </form>
            <DialogFooter><Button data-testid="save-project-button" type="submit" form="project-form" loading={busy} disabled={busy}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {pError && <Badge variant="destructive" className="mb-4">{pError}</Badge>}
      {pLoading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div> : (
        <DataTable
          columns={columns}
          data={projects || []}
          keyExtractor={(p) => p.id}
          mobileRender={mobileRender}
          emptyState={<EmptyState icon={FolderKanban} title={t('noData') || 'No projects'} description="Create your first project to get started." action={<Button size="sm" onClick={()=>setOpen(true)}><Plus className="mr-2 h-4 w-4" /> New Project</Button>} />}
        />
      )}

      <Sheet open={tasksOpen} onOpenChange={setTasksOpen}>
        <SheetContent side="right" className="w-full max-w-md border-l border-[var(--color-border)] bg-[var(--color-card)] p-0">
          <div className="flex h-full flex-col">
            <SheetHeader className="border-b border-[var(--color-border)] p-6">
              <SheetTitle className="flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-[var(--color-primary)]" />
                Tasks — {selectedProject?.name}
              </SheetTitle>
              <SheetDescription>
                Manage tasks for this project.
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-6">
              {tasksLoading ? (
                <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
              ) : taskError ? (
                <Badge variant="destructive" className="w-full justify-center">{taskError}</Badge>
              ) : tasks.length === 0 ? (
                <EmptyState icon={ListChecks} title="No tasks" description="Add the first task for this project." />
              ) : (
                <div className="space-y-2">
                  {tasks.map((task) => (
                    <div key={task.id} className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[var(--color-foreground)] truncate">{task.name}</div>
                        <div className="text-xs text-[var(--color-muted-foreground)]">
                          {task.billable ? 'Billable' : 'Non-billable'}
                          {task.rate != null && ` · CHF ${task.rate}`}
                        </div>
                      </div>
                      <Button data-testid="deactivate-task-button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-[var(--color-muted-foreground)] hover:text-destructive" onClick={() => deactivateTask(task.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <SheetFooter className="border-t border-[var(--color-border)] p-6">
              <form onSubmit={createTask} className="w-full space-y-3">
                {taskError && <Badge variant="destructive" className="w-full justify-center">{taskError}</Badge>}
                <div className="space-y-2">
                  <Label htmlFor="task-name">New task</Label>
                  <Input
                    id="task-name"
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    placeholder="e.g. Design, Development, Review"
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="task-billable"
                    type="checkbox"
                    className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                    checked={taskBillable}
                    onChange={(e) => setTaskBillable(e.target.checked)}
                  />
                  <Label htmlFor="task-billable" className="text-sm font-normal">Billable</Label>
                </div>
                <Button data-testid="add-task-button" type="submit" loading={taskBusy} disabled={taskBusy} className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Task
                </Button>
              </form>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
